import React from 'react';
import { wobble, lerp, loop, clamp, easeOut, easeInOut, pulse } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES (Anivaze v2) — "programming as a child's game"
 * Pure function of `t` (seconds). Default t=0 renders cleanly.
 * See design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ━━━ Shared tiny helpers ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

/** Draw a rounded card with a fill. */
const Card = (
  x: number, y: number, w: number, h: number,
  fill: string, stroke: string, rx = 10,
) => (
  <rect x={x} y={y} width={w} height={h} rx={rx} ry={rx}
    fill={fill} stroke={stroke} strokeWidth={2} />
);

/* ━━━ IF: weather toggles and character picks umbrella / sunglasses ━━━━━━━━━ */

export interface CodingIfSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since the scene started — pure function of t. */
  t?: number;
}

/** Draw the little character body at (cx, cy). */
const Character = (
  cx: number, cy: number,
  item: 'umbrella' | 'sunglasses' | 'none',
  blink: number,
) => {
  const eyeH = blink > 0.85 ? 0.5 : 4; // squish eye during blink
  return (
    <g>
      {/* shadow */}
      <ellipse cx={cx} cy={cy + 64} rx={30} ry={6} fill="rgba(0,0,0,0.15)" />
      {/* body */}
      <ellipse cx={cx} cy={cy + 40} rx={22} ry={26} fill="#fbbf24" stroke="#b45309" strokeWidth={2} />
      {/* head */}
      <circle cx={cx} cy={cy + 4} r={24} fill="#fde68a" stroke="#b45309" strokeWidth={2} />
      {/* eyes */}
      <ellipse cx={cx - 8} cy={cy + 2} rx={3.5} ry={eyeH} fill="#1f2937" />
      <ellipse cx={cx + 8} cy={cy + 2} rx={3.5} ry={eyeH} fill="#1f2937" />
      {/* smile */}
      <path d={`M${cx - 8},${cy + 12} Q${cx},${cy + 18} ${cx + 8},${cy + 12}`}
        fill="none" stroke="#b45309" strokeWidth={2} strokeLinecap="round" />
      {/* arms */}
      <line x1={cx - 22} y1={cy + 30} x2={cx - 40} y2={cy + 46}
        stroke="#b45309" strokeWidth={5} strokeLinecap="round" />
      <line x1={cx + 22} y1={cy + 30} x2={cx + 40} y2={cy + 46}
        stroke="#b45309" strokeWidth={5} strokeLinecap="round" />
      {/* legs */}
      <line x1={cx - 10} y1={cy + 64} x2={cx - 14} y2={cy + 84}
        stroke="#b45309" strokeWidth={5} strokeLinecap="round" />
      <line x1={cx + 10} y1={cy + 64} x2={cx + 14} y2={cy + 84}
        stroke="#b45309" strokeWidth={5} strokeLinecap="round" />

      {/* ITEM: umbrella held above head */}
      {item === 'umbrella' && (
        <g>
          {/* handle */}
          <line x1={cx} y1={cy - 18} x2={cx} y2={cy - 66}
            stroke="#6d28d9" strokeWidth={4} strokeLinecap="round" />
          {/* canopy */}
          <path d={`M${cx - 30},${cy - 66} Q${cx},${cy - 98} ${cx + 30},${cy - 66} Z`}
            fill="#7c3aed" stroke="#4c1d95" strokeWidth={2} />
          <path d={`M${cx - 30},${cy - 66} Q${cx - 15},${cy - 56} ${cx},${cy - 66}`}
            fill="#a78bfa" />
          <path d={`M${cx},${cy - 66} Q${cx + 15},${cy - 56} ${cx + 30},${cy - 66}`}
            fill="#a78bfa" />
        </g>
      )}
      {/* ITEM: sunglasses on face */}
      {item === 'sunglasses' && (
        <g>
          {/* left lens */}
          <rect x={cx - 19} y={cy - 4} width={14} height={10} rx={4}
            fill="#f59e0b" stroke="#92400e" strokeWidth={2} />
          {/* right lens */}
          <rect x={cx + 5} y={cy - 4} width={14} height={10} rx={4}
            fill="#f59e0b" stroke="#92400e" strokeWidth={2} />
          {/* bridge */}
          <line x1={cx - 5} y1={cy + 1} x2={cx + 5} y2={cy + 1}
            stroke="#92400e" strokeWidth={2} strokeLinecap="round" />
        </g>
      )}
    </g>
  );
};

export const CodingIfScene: React.FC<CodingIfSceneProps> = ({
  opacity = 1,
  accentColor = '#6366f1',
  t = 0,
  label = 'If-then: do something only when it\'s true!',
}) => {
  /*
   * 7-second loop:
   *  0.0–3.5 s → RAINY half (cloud rolls in, rain falls, umbrella active)
   *  3.5–7.0 s → SUNNY half (cloud drifts away, sun shines, sunglasses active)
   */
  const cycleLen = 7;
  const phase = loop(t, cycleLen); // 0..1

  const isRainy = phase < 0.5;        // first half = rain, second half = sun

  // Cloud X: slides in from right during rainy, exits left during sunny.
  // Rainy:  phase 0→0.5 maps to cloudX 850→340 then holds
  // Sunny:  phase 0.5→1  maps to cloudX 340→-200
  const cloudX = isRainy
    ? lerp(850, 340, clamp(phase / 0.15, 0, 1))   // quick slide in
    : lerp(340, -200, clamp((phase - 0.5) / 0.3, 0, 1));

  // Rain drops: 8 drops, staggered by index, only visible when rainy
  const rainVisible = isRainy && phase > 0.12;

  // Sun brightness: fades in during sunny phase
  const sunOpacity = isRainy ? clamp(1 - phase / 0.1, 0, 0.3) : clamp((phase - 0.5) / 0.12, 0, 1);

  // Item the character holds
  const itemPhase = isRainy ? clamp((phase - 0.12) / 0.08, 0, 1) : clamp((phase - 0.55) / 0.08, 0, 1);
  const activeItem: 'umbrella' | 'sunglasses' | 'none' =
    itemPhase > 0.5 ? (isRainy ? 'umbrella' : 'sunglasses') : 'none';

  // Gentle blink from pulse
  const blinkP = pulse(t, 3.2, 0);

  // Sun ray spin
  const sunRayAng = (t * 12) % 360;

  // IF card highlight: umbrella branch OR sunglasses branch
  const umbrellaBranchLit = isRainy && itemPhase > 0.3;
  const sunglassesBranchLit = !isRainy && itemPhase > 0.3;

  // Character gentle bob
  const charBob = wobble(t, 1.4, 2.5);

  const charCX = 220, charCY = 250 + charBob;

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="if-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={isRainy ? '#94a3b8' : '#bae6fd'} />
          <stop offset="100%" stopColor={isRainy ? '#cbd5e1' : '#e0f2fe'} />
        </linearGradient>
        <radialGradient id="if-sun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fff7cc" />
          <stop offset="70%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
      </defs>

      {/* Sky */}
      <rect x={0} y={0} width={800} height={360} fill="url(#if-sky)" />
      {/* Ground */}
      <rect x={0} y={360} width={800} height={90} fill="#86efac" />
      <rect x={0} y={360} width={800} height={8} fill="#4ade80" />

      {/* ── SUN (bottom-right quadrant of sky) ── */}
      <g transform="translate(680 100)" opacity={sunOpacity}>
        <g transform={`rotate(${sunRayAng})`} opacity={0.9}>
          {Array.from({ length: 12 }, (_, k) => {
            const a = (k / 12) * Math.PI * 2;
            return (
              <line key={k}
                x1={42 * Math.cos(a)} y1={42 * Math.sin(a)}
                x2={58 * Math.cos(a)} y2={58 * Math.sin(a)}
                stroke="#fcd34d" strokeWidth={3} strokeLinecap="round" />
            );
          })}
        </g>
        <circle r={32} fill="url(#if-sun)" />
        <circle cx={-9} cy={-8} r={5} fill="rgba(255,255,255,0.35)" />
      </g>

      {/* ── CLOUD ── */}
      <g transform={`translate(${cloudX} 90)`}>
        <ellipse cx={0} cy={0} rx={52} ry={32} fill="#f1f5f9" stroke="#cbd5e1" strokeWidth={1.5} />
        <ellipse cx={48} cy={10} rx={38} ry={26} fill="#f1f5f9" stroke="#cbd5e1" strokeWidth={1.5} />
        <ellipse cx={-42} cy={12} rx={34} ry={22} fill="#f1f5f9" stroke="#cbd5e1" strokeWidth={1.5} />
        {/* rain drops */}
        {rainVisible && Array.from({ length: 8 }, (_, i) => {
          const dropX = -40 + i * 12 + wobble(t, 0.7, 2, i);
          const dropY = 38 + ((loop(t + i * 0.18, 0.55)) * 80);
          return (
            <ellipse key={i} cx={dropX} cy={dropY} rx={3} ry={6}
              fill="#7dd3fc" opacity={0.85} />
          );
        })}
      </g>

      {/* ── CHARACTER ── */}
      {Character(charCX, charCY, activeItem, blinkP)}

      {/* ── IF DECISION CARD (right side) ── */}
      <g transform="translate(440 140)">
        {/* outer card */}
        {Card(0, 0, 310, 200, 'rgba(255,255,255,0.95)', '#c7d2fe', 14)}
        {/* header bar */}
        <rect x={0} y={0} width={310} height={42} rx={14} ry={14}
          fill={accentColor} />
        <rect x={0} y={28} width={310} height={14} fill={accentColor} />
        <text x={155} y={27} textAnchor="middle"
          fill="#fff" fontSize={17} fontWeight={800} fontFamily="Inter,sans-serif">
          IF condition…
        </text>

        {/* UMBRELLA branch */}
        <g opacity={umbrellaBranchLit ? 1 : 0.38}>
          {Card(14, 52, 282, 56, umbrellaBranchLit ? '#ede9fe' : '#f8fafc', umbrellaBranchLit ? '#7c3aed' : '#c7d2fe', 8)}
          {umbrellaBranchLit && <rect x={14} y={52} width={5} height={56} rx={3} fill="#7c3aed" />}
          <text x={42} y={75} fill="#4c1d95" fontSize={13} fontWeight={700} fontFamily="Inter,sans-serif">
            IF raining →
          </text>
          <text x={42} y={96} fill="#6d28d9" fontSize={12} fontFamily="Inter,sans-serif">
            ☂  grab umbrella
          </text>
        </g>

        {/* SUNGLASSES branch */}
        <g opacity={sunglassesBranchLit ? 1 : 0.38}>
          {Card(14, 118, 282, 56, sunglassesBranchLit ? '#fef9c3' : '#f8fafc', sunglassesBranchLit ? '#b45309' : '#c7d2fe', 8)}
          {sunglassesBranchLit && <rect x={14} y={118} width={5} height={56} rx={3} fill="#b45309" />}
          <text x={42} y={141} fill="#78350f" fontSize={13} fontWeight={700} fontFamily="Inter,sans-serif">
            ELSE →
          </text>
          <text x={42} y={162} fill="#b45309" fontSize={12} fontFamily="Inter,sans-serif">
            😎  wear sunglasses
          </text>
        </g>
      </g>

      {/* ── CAPTION ── */}
      <g transform="translate(400 428)">
        <rect x={-270} y={-20} width={540} height={36} rx={18}
          fill="rgba(255,255,255,0.9)" stroke="#c7d2fe" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle"
          fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};

/* ━━━ DEBUGGING: robot glitches at a buggy step, magnifier finds the bug ━━━━ */

export interface DebuggingSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since the scene started — pure function of t. */
  t?: number;
}

/** A tiny pixel-art bug on a card. */
const Bug = (cx: number, cy: number, legWiggle: number) => (
  <g>
    {/* body */}
    <ellipse cx={cx} cy={cy} rx={9} ry={7} fill="#ef4444" stroke="#991b1b" strokeWidth={1.5} />
    {/* spots */}
    <circle cx={cx - 3} cy={cy - 1} r={2} fill="#991b1b" />
    <circle cx={cx + 3} cy={cy + 1} r={2} fill="#991b1b" />
    {/* head */}
    <circle cx={cx + 9} cy={cy} r={5} fill="#ef4444" stroke="#991b1b" strokeWidth={1.5} />
    {/* antennae */}
    <line x1={cx + 11} y1={cy - 4} x2={cx + 16} y2={cy - 10}
      stroke="#991b1b" strokeWidth={1.5} strokeLinecap="round" />
    <line x1={cx + 13} y1={cy - 2} x2={cx + 18} y2={cy - 6}
      stroke="#991b1b" strokeWidth={1.5} strokeLinecap="round" />
    {/* legs (3 pairs) */}
    {[-4, 0, 4].map((ly, i) => (
      <g key={i}>
        <line x1={cx - 8} y1={cy + ly} x2={cx - 16 + legWiggle * (i % 2 === 0 ? 1 : -1)} y2={cy + ly + 5}
          stroke="#991b1b" strokeWidth={1.5} strokeLinecap="round" />
        <line x1={cx + 7} y1={cy + ly} x2={cx + 16 + legWiggle * (i % 2 === 0 ? -1 : 1)} y2={cy + ly + 5}
          stroke="#991b1b" strokeWidth={1.5} strokeLinecap="round" />
      </g>
    ))}
  </g>
);

/** A small robot walking at (cx, cy). */
const Robot = (cx: number, cy: number, legSwing: number, shake: number, cheer: boolean) => {
  const armAngle = cheer ? -40 : legSwing * 0.6;
  return (
    <g transform={`translate(${shake} 0)`}>
      {/* shadow */}
      <ellipse cx={cx} cy={cy + 52} rx={20} ry={5} fill="rgba(0,0,0,0.18)" />
      {/* body */}
      <rect x={cx - 16} y={cy + 10} width={32} height={30} rx={6}
        fill="#475569" stroke="#1e293b" strokeWidth={2} />
      {/* belly light */}
      <rect x={cx - 8} y={cy + 17} width={16} height={10} rx={3}
        fill={cheer ? '#86efac' : '#fbbf24'} stroke="#1e293b" strokeWidth={1} />
      {/* head */}
      <rect x={cx - 14} y={cy - 14} width={28} height={26} rx={6}
        fill="#64748b" stroke="#1e293b" strokeWidth={2} />
      {/* visor */}
      <rect x={cx - 10} y={cy - 10} width={20} height={12} rx={3}
        fill={cheer ? '#86efac' : '#bae6fd'} stroke="#1e293b" strokeWidth={1} />
      {/* eye dots */}
      <circle cx={cx - 5} cy={cy - 4} r={2.5} fill="#0f172a" />
      <circle cx={cx + 5} cy={cy - 4} r={2.5} fill="#0f172a" />
      {/* antenna */}
      <line x1={cx} y1={cy - 14} x2={cx} y2={cy - 26}
        stroke="#94a3b8" strokeWidth={3} strokeLinecap="round" />
      <circle cx={cx} cy={cy - 28} r={4} fill="#f59e0b" stroke="#b45309" strokeWidth={1} />
      {/* arms */}
      <line x1={cx - 16} y1={cy + 18}
        x2={cx - 28 + (cheer ? 8 : 0)} y2={cy + 18 + legSwing * 0.5 + (cheer ? -20 : 0)}
        stroke="#334155" strokeWidth={5} strokeLinecap="round" />
      <line x1={cx + 16} y1={cy + 18}
        x2={cx + 28 + armAngle * 0.3} y2={cy + 18 - Math.abs(legSwing) * 0.5 + (cheer ? -20 : 0)}
        stroke="#334155" strokeWidth={5} strokeLinecap="round" />
      {/* legs */}
      <line x1={cx - 8} y1={cy + 40} x2={cx - 8 + legSwing} y2={cy + 52}
        stroke="#334155" strokeWidth={5} strokeLinecap="round" />
      <line x1={cx + 8} y1={cy + 40} x2={cx + 8 - legSwing} y2={cy + 52}
        stroke="#334155" strokeWidth={5} strokeLinecap="round" />
    </g>
  );
};

export const DebuggingScene: React.FC<DebuggingSceneProps> = ({
  opacity = 1,
  accentColor = '#3b82f6',
  t = 0,
  label = 'A bug is a mistake — find it and fix it!',
}) => {
  /*
   * 8-second loop phases (loop phase p ∈ [0,1)):
   *  p 0.00–0.45 → Robot walks steps 1-2-3 (step 3 is buggy, stops)
   *  p 0.45–0.55 → Glitch: robot shakes, "?!" pops up
   *  p 0.55–0.72 → Magnifying glass slides over buggy step
   *  p 0.72–0.80 → Bug removed (poof + sparkles)
   *  p 0.80–1.00 → Step turns green, robot resumes and cheers at flag
   */
  const cycleLen = 8;
  const p = loop(t, cycleLen);

  // ── step card positions (4 cards, evenly spaced, y centred at 230)
  const cardW = 108, cardH = 62, cardGap = 22;
  const totalW = 4 * cardW + 3 * cardGap;
  const cardsStartX = (800 - totalW) / 2;
  const cardY = 200;

  // ── which step the robot is on (0..3 then "past finish")
  // During walk phase (p 0→0.45) robot steps through steps 0,1,2; freezes at step 2.
  // In fix phase (p 0.8→1) robot walks step 3 to the flag.
  const walkPhase = clamp(p / 0.45, 0, 1);   // 0→1 during 0..0.45
  const resumePhase = clamp((p - 0.80) / 0.20, 0, 1); // 0→1 during 0.8..1

  // Robot X position
  const buggyCardX = cardsStartX + 2 * (cardW + cardGap); // step card index 2
  const flagX = cardsStartX + totalW + 48;

  // Step 0..2: lerp across first 3 cards based on walkPhase
  const walkEndX = buggyCardX + cardW / 2; // stops at buggy card
  const walkStartX = cardsStartX - 30;
  const robotWalkX = lerp(walkStartX, walkEndX, easeInOut(walkPhase));

  // Step 2 → flag: only after fix
  const robotResumeX = lerp(walkEndX, flagX - 10, easeOut(resumePhase));

  const robotX = p >= 0.80 ? robotResumeX : (p < 0.45 ? robotWalkX : walkEndX);
  const robotBaseY = 280;

  // Leg swing
  const legSwing = wobble(t, 0.35, 10);
  const isWalking = (p < 0.45 && walkPhase > 0.02 && walkPhase < 0.98) || p >= 0.80;
  const legSwingActive = isWalking ? legSwing : 0;

  // Glitch phase
  const isGlitching = p >= 0.45 && p < 0.55;
  const glitchAmt = isGlitching ? wobble(t, 0.06, 5) : 0;

  // "?!" pop-up
  const qmarkOpacity = isGlitching ? clamp((p - 0.45) / 0.03, 0, 1) : 0;

  // Magnifier slide
  const magPhase = clamp((p - 0.55) / 0.17, 0, 1);
  const magVisible = p >= 0.55 && p < 0.80;
  const magTargetX = buggyCardX + cardW / 2;
  const magX = lerp(magTargetX + 180, magTargetX, easeOut(magPhase));
  const magY = cardY + cardH / 2;

  // Bug visibility: present until the "poof" at p=0.72
  const bugVisible = p < 0.72 || p >= 0.99; // reappears at very start of next loop
  const bugOpacity = p >= 0.72 && p < 0.80
    ? clamp(1 - (p - 0.72) / 0.05, 0, 1)
    : (bugVisible ? 1 : 0);

  // Sparkles after poof (p 0.72..0.82)
  const sparkleActive = p >= 0.72 && p < 0.82;

  // Step 2 card colour: turns green once bug removed (p >= 0.80)
  const step2Fixed = p >= 0.80;

  // Cheer at flag
  const cheer = p >= 0.92;

  // Bug leg wiggle
  const bugLeg = wobble(t, 0.22, 3);

  // Step progress indicators (green ticks)
  // step 0 lit after p>0.15, step 1 after p>0.30, step 2 after p>0.80
  const stepLit = [
    p > 0.15 || (p >= 0.80 && p < 1),
    p > 0.30 || (p >= 0.80 && p < 1),
    step2Fixed,
    p > 0.92,
  ];

  // Step labels
  const stepLabels = ['Step 1', 'Step 2', 'Step 3', 'Step 4'];

  // Sparkle positions derived deterministically from index (no Math.random)
  const sparkleOffsets = [
    { dx: -18, dy: -22 }, { dx: 12, dy: -28 }, { dx: 24, dy: -10 },
    { dx: -6, dy: -36 }, { dx: 18, dy: -18 },
  ];

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="dbg-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f0f9ff" />
          <stop offset="100%" stopColor="#e0f2fe" />
        </linearGradient>
        <radialGradient id="dbg-mag" cx="50%" cy="50%">
          <stop offset="0%" stopColor="rgba(186,230,253,0.35)" />
          <stop offset="80%" stopColor="rgba(186,230,253,0.08)" />
          <stop offset="100%" stopColor="rgba(186,230,253,0)" />
        </radialGradient>
      </defs>

      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="url(#dbg-bg)" />

      {/* ── Floor ── */}
      <rect x={0} y={360} width={800} height={90} fill="#dcfce7" />
      <rect x={0} y={360} width={800} height={5} fill="#86efac" />

      {/* ── Path line connecting cards ── */}
      {Array.from({ length: 3 }, (_, i) => {
        const x1 = cardsStartX + (i + 1) * (cardW + cardGap) - cardGap;
        return (
          <line key={i} x1={x1} y1={cardY + cardH / 2}
            x2={x1 + cardGap} y2={cardY + cardH / 2}
            stroke="#94a3b8" strokeWidth={3} strokeDasharray="5 4" />
        );
      })}

      {/* ── STEP CARDS ── */}
      {stepLabels.map((lbl, i) => {
        const cx = cardsStartX + i * (cardW + cardGap);
        const isBuggy = i === 2;
        const isFixed = isBuggy && step2Fixed;
        const lit = stepLit[i];
        const cardFill = isFixed ? '#dcfce7' : (isBuggy && !step2Fixed ? '#fff1f2' : (lit ? '#eff6ff' : '#f8fafc'));
        const cardStroke = isFixed ? '#16a34a' : (isBuggy && !step2Fixed ? '#fca5a5' : (lit ? accentColor : '#cbd5e1'));
        return (
          <g key={i}>
            {Card(cx, cardY, cardW, cardH, cardFill, cardStroke, 10)}
            {/* step label */}
            <text x={cx + cardW / 2} y={cardY + 24} textAnchor="middle"
              fill="#334155" fontSize={13} fontWeight={700} fontFamily="Inter,sans-serif">
              {lbl}
            </text>
            {/* tick or X icon */}
            {lit && (
              <text x={cx + cardW / 2} y={cardY + 48} textAnchor="middle"
                fill="#16a34a" fontSize={15} fontWeight={800} fontFamily="Inter,sans-serif">
                {isFixed ? '✓ fixed!' : '✓'}
              </text>
            )}
            {isBuggy && !step2Fixed && !lit && (
              <text x={cx + cardW / 2} y={cardY + 48} textAnchor="middle"
                fill="#ef4444" fontSize={13} fontFamily="Inter,sans-serif">
                hmm…
              </text>
            )}
          </g>
        );
      })}

      {/* ── BUG on step card 2 ── */}
      <g opacity={bugOpacity}>
        {Bug(
          cardsStartX + 2 * (cardW + cardGap) + cardW / 2 + 8,
          cardY - 14,
          bugLeg,
        )}
      </g>

      {/* ── SPARKLES after poof ── */}
      {sparkleActive && sparkleOffsets.map((s, i) => {
        const sp = clamp((p - 0.72) / 0.10, 0, 1);
        const sOpacity = clamp(1 - sp * 1.4, 0, 1);
        const sScale = 0.4 + sp * 1.2;
        return (
          <g key={i}
            transform={`translate(${cardsStartX + 2 * (cardW + cardGap) + cardW / 2 + s.dx + s.dx * sp * 0.6} ${cardY - 14 + s.dy + s.dy * sp * 0.6}) scale(${sScale})`}
            opacity={sOpacity}>
            <line x1={0} y1={-6} x2={0} y2={6} stroke="#fbbf24" strokeWidth={2} strokeLinecap="round" />
            <line x1={-6} y1={0} x2={6} y2={0} stroke="#fbbf24" strokeWidth={2} strokeLinecap="round" />
            <line x1={-4} y1={-4} x2={4} y2={4} stroke="#a78bfa" strokeWidth={1.5} strokeLinecap="round" />
            <line x1={4} y1={-4} x2={-4} y2={4} stroke="#a78bfa" strokeWidth={1.5} strokeLinecap="round" />
          </g>
        );
      })}

      {/* ── FLAG ── */}
      <g transform={`translate(${flagX} ${cardY - 10})`}>
        <line x1={0} y1={0} x2={0} y2={cardH + 140} stroke="#475569" strokeWidth={4} strokeLinecap="round" />
        <path d="M0,0 L38,12 L0,24 Z" fill="#22c55e" stroke="#15803d" strokeWidth={1.5} />
        <text x={4} y={18} fill="#fff" fontSize={10} fontWeight={800} fontFamily="Inter,sans-serif">DONE</text>
      </g>

      {/* ── ROBOT ── */}
      {Robot(robotX, robotBaseY, legSwingActive, glitchAmt, cheer)}

      {/* ── "?!" POP-UP above robot ── */}
      <g opacity={qmarkOpacity} transform={`translate(${robotX} ${robotBaseY - 40})`}>
        <rect x={-22} y={-28} width={44} height={30} rx={8}
          fill="#fef2f2" stroke="#ef4444" strokeWidth={2} />
        <text x={0} y={-7} textAnchor="middle"
          fill="#dc2626" fontSize={16} fontWeight={900} fontFamily="Inter,sans-serif">
          ?!
        </text>
        {/* speech bubble tail */}
        <path d="M-6,-1 L6,-1 L0,8 Z" fill="#fef2f2" stroke="#ef4444" strokeWidth={1} />
      </g>

      {/* ── MAGNIFYING GLASS ── */}
      {magVisible && (
        <g transform={`translate(${magX} ${magY})`}>
          {/* lens area glow */}
          <circle cx={0} cy={0} r={50} fill="url(#dbg-mag)" />
          {/* handle */}
          <line x1={32} y1={32} x2={58} y2={58}
            stroke="#92400e" strokeWidth={8} strokeLinecap="round" />
          {/* rim */}
          <circle cx={0} cy={0} r={38}
            fill="none" stroke="#b45309" strokeWidth={5} />
          {/* glass tint */}
          <circle cx={0} cy={0} r={36}
            fill="rgba(186,230,253,0.28)" />
          {/* sheen */}
          <ellipse cx={-12} cy={-14} rx={8} ry={5}
            fill="rgba(255,255,255,0.55)" transform="rotate(-30 -12 -14)" />
        </g>
      )}

      {/* ── CAPTION ── */}
      <g transform="translate(400 428)">
        <rect x={-290} y={-20} width={580} height={36} rx={18}
          fill="rgba(255,255,255,0.92)" stroke="#bfdbfe" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle"
          fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};
